/**
 * Sam, the AI assistant, in the Aperio portal (owner, 2026-10-26). The chat
 * box is our own (owner, 2026-10-27: no "Powered by" banner): the marketing
 * site's sam-chat.js and the ElevenLabs SDK it talks through, both
 * self-hosted in public/aperio/ (byte-checked by aperio:check) and fetched
 * only when the visitor taps "Chat with Sam" (components/SamChat): no page
 * view pays for them and nothing reaches ElevenLabs before the visitor asks.
 * On the fleet dashboard (/fleet...) the fleet team's agent answers, as on
 * the site's fleet pages; either chat can switch to the other.
 */
export const SAM_AGENT_ID = 'agent_3501m5wskxxtfsctcfsrm1s3c80r';
export const FLEET_AGENT_ID = 'agent_5201m5y1m17gfdwsehftv2hg436v';
export const SAM_CHAT_SRC = '/aperio/sam-chat.js';

/** What a signed-in customer tells Sam; blank = a visitor we don't know. */
export interface SamVariables {
  'first_name': string;
  'customer_status': string;
  vehicle: string;
}

export const BLANK_SAM_VARIABLES: SamVariables = {
  first_name: '',
  customer_status: 'unknown',
  vehicle: '',
};

/** sam-chat.js's API (window.AperioChat). */
export interface AperioChatApi {
  start(options: {
    samAgentId: string;
    fleetAgentId: string;
    team: 'sam' | 'fleet';
    page: string;
    path: string;
    lang: string;
    known?: Partial<SamVariables>;
    onClose?: () => void;
  }): { close(): void; element: HTMLElement };
  pageKind(path: string): string;
}

const chatApi = (): AperioChatApi | undefined => (
  typeof window === 'undefined'
    ? undefined
    : (window as unknown as { AperioChat?: AperioChatApi }).AperioChat
);

let loading: Promise<AperioChatApi> | null = null;

/** Loads the chat box script once. */
export function loadSamWidget(): Promise<AperioChatApi> {
  const ready = chatApi();
  if (ready) return Promise.resolve(ready);
  if (!loading) {
    loading = new Promise<AperioChatApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SAM_CHAT_SRC;
      script.async = true;
      script.onload = () => {
        const api = chatApi();
        if (api) resolve(api);
        else {
          loading = null;
          reject(new Error('Sam chat did not load'));
        }
      };
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('Sam chat did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

/**
 * Opens the chat box: the fleet team on a /fleet page, else Sam with what
 * we know about the signed-in customer. sam-chat.js fills every variable
 * either agent's prompt reads, `greeting` included.
 */
export function openSamWidget(
  api: AperioChatApi,
  known: SamVariables,
  lang: string,
  onClose?: () => void,
): { close(): void; element: HTMLElement } {
  const path = window.location.pathname || '/';
  const page = api.pageKind(path);
  return api.start({
    samAgentId: SAM_AGENT_ID,
    fleetAgentId: FLEET_AGENT_ID,
    team: page ? 'fleet' : 'sam',
    page,
    path,
    lang: String(lang || 'en').slice(0, 2),
    known,
    onClose,
  });
}

/**
 * What sits fixed along the bottom of the screen and must stay clear of
 * the launcher: the consumer skin's booking bar (fixed on a phone), the old
 * flow's Back / Next bar (sticky) and the consent bar.
 */
export const SAM_BOTTOM_BARS = '.apr-c-bar, .apr-step__footer, .apr-consent';

/**
 * How far up the launcher has to sit to clear those bars: the distance
 * from the top of the highest one that is pinned to the bottom of the
 * screen down to the screen's bottom edge, or 0 when none is (a desktop,
 * where the booking bar is in the page).
 */
export function samBottomClearance(doc: Document = document): number {
  const view = doc.defaultView;
  if (!view) return 0;
  const height = view.innerHeight;
  let top = height;
  doc.querySelectorAll<HTMLElement>(SAM_BOTTOM_BARS).forEach((el) => {
    const { position } = view.getComputedStyle(el);
    if (position !== 'fixed' && position !== 'sticky') return;
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.top >= height) return;
    // Pinned to the bottom (or stacked on a bar that is), not mid-page.
    if (rect.bottom < height - 240) return;
    top = Math.min(top, rect.top);
  });
  return Math.max(0, Math.ceil(height - top));
}
